import { useState, useEffect } from 'react'
import { useWhatsNew } from '../hooks/useWhatsNew'
import { useAuth } from '../hooks/useAuth'
import { cn } from '../lib/utils'
import { Portal } from './Portal'

const renderBody = (text) =>
  text
    .split('\n\n')
    .filter(Boolean)
    .map((para, i) => {
      const html = para
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>')
      return (
        <p key={i} className="text-sm text-text-secondary leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }} />
      )
    })

const PostUpdateModal = ({ onClose, onPost }) => {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [version, setVersion] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handlePost = async () => {
    if (!title.trim() || !body.trim()) return setError('Title and body are required')
    setSaving(true)
    try {
      await onPost({ title: title.trim(), body: body.trim(), version: version.trim() || null })
      onClose()
    } catch (e) {
      setError(e.message ?? 'Failed to post')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto p-6 space-y-4">
        <h2 className="font-bold text-text-primary text-lg">Post update</h2>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <input
          type="text"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="Version (optional, e.g. 1.4.2)"
          className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What's changed? **Bold** and *italic* supported."
          rows={5}
          className="w-full border border-border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handlePost}
            disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}

export const WhatsNewPanel = ({ onClose }) => {
  const { data: entries = [], isLoading, markSeen, post, unreadCount } = useWhatsNew()
  const { user } = useAuth()
  const isSuperAdmin = user?.app_metadata?.is_super_admin ?? false
  const [postModal, setPostModal] = useState(false)

  // Mark all visible entries as seen when panel opens
  useEffect(() => {
    entries.forEach((e) => {
      const seenBy = Array.isArray(e.seen_by) ? e.seen_by : []
      if (!seenBy.includes(user?.id)) {
        markSeen.mutate(e.id)
      }
    })
  }, [entries.length])

  return (
    <Portal>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div className="fixed top-14 right-4 z-50 w-80 bg-surface rounded-2xl shadow-card border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-text-primary text-sm">What's new</h2>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button
                onClick={() => setPostModal(true)}
                className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-lg font-medium"
              >
                Post update
              </button>
            )}
            <button onClick={onClose} className="text-text-muted hover:text-text-secondary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isLoading && (
            <div className="p-4 space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse space-y-2">
                  <div className="h-4 bg-border rounded w-3/4" />
                  <div className="h-3 bg-border rounded w-full" />
                  <div className="h-3 bg-border rounded w-5/6" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && entries.length === 0 && (
            <div className="p-6 text-center text-sm text-text-muted">
              Nothing to show yet.
            </div>
          )}

          {!isLoading && entries.map((entry, i) => {
            const seenBy = Array.isArray(entry.seen_by) ? entry.seen_by : []
            const isUnread = !seenBy.includes(user?.id)
            const date = new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

            return (
              <div key={entry.id} className={cn('px-4 py-4', i < entries.length - 1 && 'border-b border-border/60')}>
                <div className="flex items-start gap-2 mb-1">
                  {isUnread && (
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-text-primary text-sm">{entry.title}</p>
                      {entry.version && (
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono">
                          v{entry.version}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mb-2">{date}</p>
                    <div className="space-y-1">{renderBody(entry.body)}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {postModal && (
        <PostUpdateModal
          onClose={() => setPostModal(false)}
          onPost={(fields) => post.mutateAsync(fields)}
        />
      )}
    </Portal>
  )
}

// ── Bell button for Header ─────────────────────────────────────────────────
export const WhatsNewButton = () => {
  const { unreadCount } = useWhatsNew()
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-text-secondary hover:bg-background transition-colors"
        aria-label="What's new"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
        )}
      </button>
      {open && <WhatsNewPanel onClose={() => setOpen(false)} />}
    </>
  )
}
